import * as dueAtSigningActionCreators from '@makemydeal/dr-dash-store/actions/dueAtSigningActionCreators';
import * as dueAtSigningSelectors from '@makemydeal/dr-dash-store/selectors/dueAtSigningSelectors';
import type { DasOverrides, StateTree } from '@makemydeal/dr-dash-types';
import type { MouseEvent, MouseEventHandler, ReactNode } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { SLIDEOUT_FULL_WIDTH_BREAKPOINT } from '../constants';
import { summaryDueAtSigningSlideOutContainerStyle, InlineGroupContainer } from '../dealSummary/common/ComponentLabelStyle';
import AmountDueAtSigningPaidByTable from './AmountDueAtSigningPaidByTable.interstate';
import AmountDueAtSigningTable from './AmountDueAtSigningTable.interstate';
import CashAmountPaid from './CashAmountPaid.interstate';
import CashFields from './CashFields.interstate';
import DueAtSigningMessages from './SummaryDueAtSigningMessages';
import EquityAllocationOrder from './EquityAllocationOrder.interstate';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import TargetCashAmountPaidCheckbox from './TargetCashAmountPaidCheckbox.interstate';
import { SlideOutWithFixedFooter } from '../utils/SlideOutWithFixedFooter';

type DasSlideOutProps = {
    show: boolean;
    onHide: MouseEventHandler;
    overrides: DasOverrides;
    header?: string;
    children?: ReactNode;
};

const SummaryDueAtSigningSlideOut: React.FC<DasSlideOutProps> = ({ show, onHide, overrides }) => {
    const dispatch = useDispatch();
    const isDasLocked = useSelector(dueAtSigningSelectors.getIsDasLocked);
    const dasErrors = useSelector(dueAtSigningSelectors.getDasErrors);
    const isDasApplying = useSelector(dueAtSigningSelectors.getIsApplying);
    const isEquityAllocationOrderEnabled = useSelector(featureToggleSelectors.isEquityAllocationOrderEnabled);
    const [hasCashAmountPaidErrors, setHasCashAmountPaidErrors] = useState(false);
    const dasAbortController = useRef<AbortController | null>(null);
    const defaultOverrides = useRef<any>(null);

    if (defaultOverrides.current === null) {
        defaultOverrides.current = overrides ? structuredClone(overrides) : null;
    }

    const selectorWithArgs = useMemo(
        () => (state: StateTree) => dueAtSigningSelectors.isDasStateModified(state, defaultOverrides.current),
        []
    );

    const dasStateModified = useSelector(selectorWithArgs);

    const isSmallScreen = useMediaQuery({ query: `(max-width: ${SLIDEOUT_FULL_WIDTH_BREAKPOINT})` });

    const allowDasApply = dasStateModified && !isDasLocked && !isDasApplying && !dasErrors.length && !hasCashAmountPaidErrors;

    const handleApplyChanges = () => {
        if (allowDasApply) {
            dasAbortController.current = new AbortController();
            dispatch(dueAtSigningActionCreators.getDueAtSigningApply(dasAbortController.current.signal));
        }
    };

    const handleHideSlideOut = (e: MouseEvent<HTMLButtonElement>) => {
        dasAbortController.current?.abort();
        onHide?.(e);
        dasAbortController.current = null;
    };

    return (
        <SlideOutWithFixedFooter
            show={show}
            onHide={handleHideSlideOut}
            header="Due At Signing"
            position="right"
            data-testid="summary-due-at-signing-slideout"
            panelWidth={isSmallScreen ? '100%' : '50%'}
            footer={{
                layout: 'rightAligned',
                options: [
                    {
                        action: handleHideSlideOut,
                        label: 'Cancel'
                    }
                ],
                primary: {
                    action: handleApplyChanges,
                    isLoading: isDasApplying,
                    label: isDasLocked && !isDasApplying ? 'Calculating...' : 'Apply',
                    disabled: !allowDasApply
                }
            }}
        >
            <div style={summaryDueAtSigningSlideOutContainerStyle}>
                <DueAtSigningMessages />
                {isEquityAllocationOrderEnabled ? (
                    <>
                        <EquityAllocationOrder />
                        <CashFields variant="slideout" onInputErrors={(message) => setHasCashAmountPaidErrors(!!message)} />
                    </>
                ) : (
                    <InlineGroupContainer>
                        <CashAmountPaid onInputErrors={(message) => setHasCashAmountPaidErrors(!!message)} />
                    </InlineGroupContainer>
                )}
                <TargetCashAmountPaidCheckbox />
                <AmountDueAtSigningTable />
                <AmountDueAtSigningPaidByTable />
            </div>
        </SlideOutWithFixedFooter>
    );
};

export default SummaryDueAtSigningSlideOut;
